import { atom } from 'jotai'
import { type DexOverlayInstance } from '../models/overlay.model'
import {
  selectedDependencyIdAtom,
  selectedElementIdsAtom,
} from './pattern-state'

export const openedOverlaysAtom = atom<DexOverlayInstance[]>([])
export const backdropClickedAtom = atom<React.MouseEvent | boolean>(false)
export const closeOverlaysAtom = atom<boolean>(false)

// Pinned details panel state
export const isPanelPinnedAtom = atom<boolean>(false)

/**
 * True when the currently displayed edit component inside the pinned panel
 * has unsaved changes. Each edit component sets this to true/false while pinned
 * so the panel can warn before switching entities.
 */
export const pinnedPanelHasChangesAtom = atom<boolean>(false)

/**
 * Stores the current resizable-panel size so it survives component remounts
 * (overlay ↔ pinned transitions, element-to-element switches).
 * Written by useResizablePanel; read on mount for initial size.
 */
export const resizablePanelSizeAtom = atom<{
  width: number
  height: number
} | null>(null)

/**
 * Per-key textarea height atoms. Each textarea subscribes only to its own
 * key, so resizing one does not re-render others.
 */
export const textareaSizeAtoms = new Map<
  string,
  ReturnType<typeof atom<number | null>>
>()

export function getTextareaSizeAtom(key: string) {
  let a = textareaSizeAtoms.get(key)
  if (!a) {
    a = atom<number | null>(null)
    textareaSizeAtoms.set(key, a)
  }
  return a
}

/**
 * Derived read-only atom — the pinned panel content always reflects the
 * current selection state, regardless of how the selection changed
 * (canvas click, matrix view, element list, …).
 */
export const pinnedPanelEntityAtom = atom(get => {
  if (!get(isPanelPinnedAtom)) return null

  const dependencyId = get(selectedDependencyIdAtom)
  if (dependencyId) {
    return { entityType: 'dependency' as const, entityId: dependencyId }
  }

  const elementIds = get(selectedElementIdsAtom)
  if (elementIds.length > 1) {
    return { entityType: 'elements' as const, entityIds: elementIds }
  }
  if (elementIds.length === 1) {
    return { entityType: 'element' as const, entityId: elementIds[0] }
  }

  return null
})
